import * as angular from 'angular';

import { IGroupSelectionService, ICurrentApplicationService } from '../../services';
import { LogbookTableConfiguration } from './LogbookTable.configuration';
import { PropertyType, ContextualField, ComponentDefinition, TimeInterval, TimePeriods, TimePeriod } from '../../models';
import { DashboardThumbnailWidget } from '../../widgets/dashboardThumbnail/dashboardThumbnail.widget';
import * as Fields from '../../common/helpers/contextual';
import { timeRangeRetroCompatibility } from './timeRangeRetroCompatibility';
import { ReadMode } from '../../common/helpers/contextual';

class LogbookTableContextualController implements angular.IComponentController, angular.IOnInit {
    configuration: LogbookTableConfiguration;
    filters: ContextualField[];
    properties: ContextualField[];

    readonly dashboards: DashboardThumbnailWidget[];
    readonly formController: angular.IFormController;

    static $inject = [
        'currentApplicationService',
        'groupSelectionService'
    ];

    constructor(
        private _currentApplicationService: ICurrentApplicationService,
        private _groupSelectionService: IGroupSelectionService
    ) {
    }

    $onInit() {
        const app = this._currentApplicationService.getApplicationConfiguration();
        const interval = new TimeInterval();
        interval.interval = app.interval;
        interval.unit = app.intervalTimeUnit;
        const duration = interval.duration();
        const defaultIntervalInSeconds = duration.asSeconds();

        const boundDisabled = this._groupSelectionService.selectedGroup() === null || this._groupSelectionService.selectedGroup() === undefined;
        if (this.configuration.alignedOn === undefined) {
            this.configuration.alignedOn = 0;
        }
        // #40211: Force checked value by default, for retro-compatibility
        if (this.configuration.hasDescription === undefined || this.configuration.hasDescription === null) {
            this.configuration.hasDescription = true;
        }
        // #44352: Ensure retro-compatibility for time range
        this.configuration.timeRange = timeRangeRetroCompatibility(this.configuration);
        const maxNumberOfTotalItems = angular.copy(Fields.MaxNumberOfTotalItems);
        maxNumberOfTotalItems.label = 'Max number of events';
        maxNumberOfTotalItems.placeholder = 'Maximum logbook entries';
        const maxNumberOfItems = angular.copy(Fields.MaxNumberOfItems);
        maxNumberOfItems.label = 'Max number of events';
        maxNumberOfItems.placeholder = 'Maximumnumber of similar logbook entries';
        const frequencyValidationRules = [
            {
                key: 'format',
                isValid: freq => {
                    const expr = new RegExp('^[0-9]*$');
                    const isValid = expr.test(freq?.interval.toString() ?? '');
                    return isValid;
                }
            },
            {
                key: 'value',
                isValid: freq => {
                    const value = freq.intervalTimeUnit;
                    const newInterval = new TimeInterval();
                    newInterval.interval = freq.interval;
                    newInterval.unit = (value || value === 0)
                        ? value
                        : this.configuration.minPeriod.unit;
                    const duration = newInterval.duration();
                    const isValid = duration.asSeconds() % defaultIntervalInSeconds === 0;
                    return isValid;
                }
            }
        ];
        this.properties = [
            {
                modelName: 'prefilterEntries',
                propertyType: PropertyType.Boolean,
                label: 'Filter and prefill new logbook entries',
                propertyName: 'prefilterEntriesProperty',
                placeholder: 'Filter entries',
                required: false,
                icon: 'fa-book',
                extraData: {}
            },
            angular.copy(Fields.RowSpacingSelect),
            maxNumberOfTotalItems,
            angular.copy(ReadMode),
            {
                modelName: 'minPeriod',
                propertyType: PropertyType.Frequency,
                label: 'Minimum period',
                propertyName: 'minPeriod',
                required: false,
                icon: 'fa-calendar-minus-o',
                extraData: {
                    intervalUnitName: 'unit',
                    frequencyValidationRules: frequencyValidationRules
                }
            }, {
                modelName: 'maxPeriod',
                propertyType: PropertyType.Frequency,
                label: 'Maximum period',
                propertyName: 'maxPeriod',
                required: false,
                icon: 'fa-calendar-plus-o',
                extraData: {
                    intervalUnitName: 'unit',
                    frequencyValidationRules: frequencyValidationRules
                }
            },
            angular.copy(Fields.PeriodReference),
            angular.copy(Fields.IsEndDateIncluded),
            {
                modelName: 'hasSeverity',
                propertyType: PropertyType.Boolean,
                label: 'Severity',
                placeholder: 'Severity',
                propertyName: 'hasSeverityProperty',
                required: false,
                icon: 'fa-bell'
            }, {
                modelName: 'timeRange',
                propertyType: PropertyType.Select,
                label: 'Time period',
                placeholder: 'Time period',
                propertyName: 'timeRangeProperty',
                required: false,
                icon: 'fa-arrows-h',
                onChange: (value: TimePeriod) => {
                    delete this.configuration.hasTimeRange;
                },
                extraData: {
                    options: TimePeriods
                }
            }, {
                modelName: 'hasDuration',
                propertyType: PropertyType.Boolean,
                label: 'Duration (h)',
                placeholder: 'Duration (h)',
                propertyName: 'hasDurationProperty',
                required: false,
                icon: 'fa-hourglass-end'
            },
            angular.copy(Fields.GroupNameSelector),
            {
                modelName: 'hasIdentifierName',
                propertyType: PropertyType.Boolean,
                label: 'Identifier name',
                placeholder: 'Identifier name',
                propertyName: 'hasIdentifierNameProperty',
                required: false,
                icon: 'fa-id-badge'
            }, {
                modelName: 'hasIdentifierShortComment',
                propertyType: PropertyType.Boolean,
                label: 'Identifier short comment',
                placeholder: 'Identifier short comment',
                propertyName: 'hasIdentifierShortCommentProperty',
                required: false,
                icon: 'fa-comment-o'
            }, {
                modelName: 'hasDescription',
                propertyType: PropertyType.Boolean,
                label: 'Description',
                placeholder: 'Description',
                propertyName: 'hasDescriptionProperty',
                required: false,
                icon: 'fa-commenting-o'
            }, {
                modelName: 'hasCategories',
                propertyType: PropertyType.Boolean,
                label: 'Categories',
                placeholder: 'Categories',
                propertyName: 'hasCategoriesProperty',
                required: false,
                icon: 'fa-tags'
            }, {
                modelName: 'hasUser',
                propertyType: PropertyType.Boolean,
                label: 'User',
                placeholder: 'User',
                propertyName: 'hasUserProperty',
                required: false,
                icon: 'fa-user'
            }, {
                modelName: 'hasDateModified',
                propertyType: PropertyType.Boolean,
                label: 'Show last modification date',
                placeholder: 'Modified on',
                propertyName: 'hasDateModifiedProperty',
                required: false,
                icon: 'fa-calendar'
            },
            {
                modelName: 'wrapIdentifierText',
                propertyType: PropertyType.Boolean,
                label: 'Wrap text of digital identifiers',
                placeholder: 'Wrap text of digital identifiers',
                propertyName: 'wrapIdentifierTextProperty',
                required: false,
                icon: 'fa-share-square-o fa-rotate-180'
            },
            angular.copy(Fields.Dashboard),
            angular.copy(Fields.DateFormat),
            angular.copy(Fields.NameConfiguration.HasGroupName),
            angular.copy(Fields.NameConfiguration.HasName),
            angular.copy(Fields.NameConfiguration.HasShortComment),
            angular.copy(Fields.NameConfiguration.HasUnit),
            angular.copy(Fields.NameConfiguration.HasValueType),
            {
                modelName: 'hasAggregationMode',
                propertyType: PropertyType.Boolean,
                label: 'Aggregation mode',
                placeholder: 'Aggregation mode',
                propertyName: 'hasAggregationModeProperty',
                required: false,
                icon: 'fa-calculator',
                extraData: { subProperty: 'nameConfiguration' }
            },
            {
                modelName: 'hasPreviousIncluded',
                propertyType: PropertyType.Boolean,
                label: 'Include previous events',
                propertyName: 'hasPreviousIncludedProperty',
                placeholder: 'Show previous logbook entries',
                required: false,
                icon: 'fa-backward',
                extraData: {}
            },
            maxNumberOfItems,
            angular.copy(Fields.IsDependentGroupsIncluded),
        ];
        const groupGuidsProperty = Object.assign({}, Fields.GroupGuids, {
            required: false,
            disabled: this.configuration.filters.isBoundToGroup
        });
        const groupTypesProperty = angular.copy(Fields.GroupTypes);
        groupTypesProperty.disabled = this.configuration.filters.isBoundToGroup;
        this.filters = [
            {
                modelName: 'isBoundToGroup',
                propertyType: PropertyType.Boolean,
                label: 'Bind',
                placeholder: 'Bind',
                propertyName: 'isBoundToGroupProperty',
                required: false,
                icon: 'fa-anchor',
                disabled: boundDisabled,
                extraData: {
                },
                onChange: (value: boolean) => {
                    groupGuidsProperty.disabled = value;
                    groupTypesProperty.disabled = value;
                }
            },
            angular.copy(Fields.IncludeChildren),
            groupGuidsProperty,
            groupTypesProperty,
            {
                modelName: 'isAlwaysShow',
                propertyType: PropertyType.Boolean,
                label: 'Always show',
                placeholder: 'Always show',
                propertyName: 'isAlwaysShowProperty',
                required: false,
                icon: 'fa-eye'
            },
            {
                modelName: 'severities',
                propertyType: PropertyType.Severities,
                label: 'Severity',
                propertyName: 'severitiesProperty',
                required: false,
                icon: 'fa-bell',
                extraData: {
                }
            }, {
                modelName: 'categories',
                propertyType: PropertyType.Categories,
                label: 'Categories',
                propertyName: 'categoriesProperty',
                required: false,
                icon: 'fa-tags',
                extraData: {
                }
            }, {
                modelName: 'sortBy',
                propertyType: PropertyType.SortBy,
                label: 'Sort by',
                propertyName: 'sortByProperty',
                required: false,
                icon: 'fa-sort-amount-desc',
                extraData: {
                }
            }, {
                modelName: 'dateOrder',
                propertyType: PropertyType.DateOrder,
                label: 'Dates sorting',
                propertyName: 'dateOrderProperty',
                required: false,
                icon: 'fa-sort-numeric-asc',
                extraData: {
                }
            }];
    }
}

export const wtLogbookTableContextual: ComponentDefinition = {
    componentId: 'wtLogbookTableContextual',
    controller: LogbookTableContextualController,
    controllerAs: 'vm',
    template: require('./logbookTable.contextual.html'),
    bindings: {
        configuration: '=',
        dashboards: '<',
        formController: '<'
    }
};